import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Banner from '../components/common/Banner';
import SectionTitle from '../components/common/SectionTitle';
import Accordion from '../components/common/Accordion';
import Timeline from '../components/common/Timeline';
import CallToAction from '../components/common/CallToAction';
import { Check, FileText, BookOpen, Users, Award, BookMarked } from 'lucide-react';

// Service data
const serviceData = {
  'counselling': {
    title: 'Admission Counselling',
    subtitle: 'Personalized guidance for your veterinary education journey',
    icon: <Users size={32} />,
    description: 'Our admission counselling service provides expert guidance to help you navigate the complex process of BVSc admissions in India. We offer personalized advice based on your academic profile, preferences, and career goals.',
    backgroundImage: 'https://images.pexels.com/photos/7089401/pexels-photo-7089401.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    features: [
      'One-on-one counselling sessions with veterinary education experts',
      'Detailed analysis of your academic profile and eligibility',
      'College recommendations based on NEET score, budget, and preferences',
      'Information on curriculum, specializations, and career prospects',
      'Guidance on financial planning for veterinary education',
      'Post-admission support and orientation'
    ],
    process: [
      { step: 1, title: 'Initial Consultation', description: 'A detailed discussion to understand your academic background, career goals, and preferences.' },
      { step: 2, title: 'Profile Analysis', description: 'Assessment of your academic records, NEET score, and eligibility for various colleges.' },
      { step: 3, title: 'College Recommendations', description: 'Personalized list of colleges that match your profile, budget, and preferences.' },
      { step: 4, title: 'Decision Making', description: 'Guidance to help you make an informed decision about your veterinary education.' },
      { step: 5, title: 'Application Support', description: 'Assistance with the application process for your chosen colleges.' }
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'When should I start seeking admission counselling?',
        content: 'Ideally, you should start seeking counselling as soon as you decide to pursue veterinary science, even before appearing for the NEET exam. Early counselling helps you understand the admission process, prepare effectively, and plan your strategy in advance. However, post-NEET counselling is also valuable for making informed decisions based on your actual scores.'
      },
      {
        id: 'faq-2',
        title: 'How do you determine which colleges are best for me?',
        content: 'We consider multiple factors including your NEET score, academic history, budget constraints, location preferences, specific interests within veterinary science, and long-term career goals. We also account for factors like college infrastructure, faculty expertise, clinical exposure, and placement records to provide holistic recommendations.'
      },
      {
        id: 'faq-3',
        title: 'Do you provide guidance on government college admissions as well?',
        content: 'Yes, our counselling covers both government and private veterinary colleges. We provide detailed information about the state and central counselling processes for government colleges, along with guidance on management quota and NRI quota admissions in private institutions.'
      }
    ]
  },
  'application': {
    title: 'Application Assistance',
    subtitle: 'Comprehensive support for the BVSc application process',
    icon: <FileText size={32} />,
    description: 'Our application assistance service ensures that your applications to veterinary colleges are complete, accurate, and submitted on time. We help you navigate the complex application procedures of multiple colleges.',
    backgroundImage: 'https://images.pexels.com/photos/6615076/pexels-photo-6615076.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    features: [
      'Guidance on application procedures for multiple colleges',
      'Assistance with form filling and document preparation',
      'Review of application materials for completeness and accuracy',
      'Timely submission of applications to meet deadlines',
      'Follow-up with colleges regarding application status',
      'Support during counseling and seat allotment process'
    ],
    process: [
      { step: 1, title: 'Application Planning', description: 'Creating a timeline and checklist for applications to different colleges.' },
      { step: 2, title: 'Document Preparation', description: 'Assistance with organizing and preparing all required documents.' },
      { step: 3, title: 'Form Filling', description: 'Step-by-step guidance for accurately completing application forms.' },
      { step: 4, title: 'Review and Submission', description: 'Thorough review of applications before final submission to colleges.' },
      { step: 5, title: 'Application Tracking', description: 'Regular follow-up with colleges regarding your application status.' }
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'How many colleges can I apply to through your service?',
        content: 'There is no limit to the number of colleges you can apply to through our service. We recommend applying to multiple colleges to increase your chances of admission. Our team will help you prioritize colleges based on your preferences and eligibility.'
      },
      {
        id: 'faq-2',
        title: 'What if I miss an application deadline?',
        content: 'We make every effort to ensure that all deadlines are met. We maintain a comprehensive calendar of application deadlines for different colleges and provide timely reminders. However, if a deadline is missed due to unforeseen circumstances, we help you explore alternative options and late application possibilities where available.'
      },
      {
        id: 'faq-3',
        title: 'Do you help with online applications and payment of application fees?',
        content: 'Yes, we provide complete assistance with online application processes, including form filling, document uploading, and guidance on fee payment procedures. However, the actual payment must be made by you directly to maintain security and comply with payment gateway requirements.'
      }
    ]
  },
  'document': {
    title: 'Documentation Support',
    subtitle: 'Expert help with organizing and preparing admission documents',
    icon: <BookMarked size={32} />,
    description: 'Our documentation support service ensures that all your admission documents are properly prepared, organized, and verified. We help you avoid common documentation errors that can delay or jeopardize your admission.',
    backgroundImage: 'https://images.pexels.com/photos/5699516/pexels-photo-5699516.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    features: [
      'Comprehensive checklist of required documents for admission',
      'Verification of documents for accuracy and completeness',
      'Guidance on obtaining necessary certificates and attestations',
      'Assistance with preparing affidavits and declarations',
      'Document organization for different stages of the admission process',
      'Support with translation of documents if required'
    ],
    process: [
      { step: 1, title: 'Document Assessment', description: 'Reviewing your existing documents and identifying any missing items.' },
      { step: 2, title: 'Document Preparation', description: 'Guidance on obtaining missing documents and ensuring proper formatting.' },
      { step: 3, title: 'Verification and Attestation', description: 'Assistance with getting documents verified and attested as required.' },
      { step: 4, title: 'Organization', description: 'Properly organizing documents as per college requirements for easy submission.' },
      { step: 5, title: 'Final Review', description: 'Thorough check of all documents before submission to ensure completeness.' }
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'What documents are typically required for BVSc admissions?',
        content: 'Common documents include NEET score card, 10th and 12th mark sheets and certificates, transfer certificate, character certificate, category certificate (if applicable), domicile certificate, passport-sized photographs, ID proof, and income certificate (if applying for scholarships). Additional documents may be required by specific colleges.'
      },
      {
        id: 'faq-2',
        title: 'How long does it take to prepare all the necessary documents?',
        content: 'The timeframe varies depending on which documents you already have and which need to be obtained. Generally, we recommend starting the document preparation process at least 2-3 months before the expected admission season. Some certificates like domicile or income certificates can take time to process through government offices.'
      },
      {
        id: 'faq-3',
        title: 'What if I discover some discrepancies in my documents?',
        content: 'If we identify any discrepancies during document verification, we guide you through the correction process. This may involve approaching the issuing authority for corrections or obtaining clarification letters. For minor issues, we advise on acceptable solutions that comply with admission requirements.'
      }
    ]
  },
  'interview': {
    title: 'Interview Preparation',
    subtitle: 'Comprehensive training for veterinary college interviews',
    icon: <BookOpen size={32} />,
    description: 'Our interview preparation service helps you present yourself confidently and effectively during veterinary college interviews. We provide comprehensive training on common interview questions, veterinary topics, and communication skills.',
    backgroundImage: 'https://images.pexels.com/photos/5699456/pexels-photo-5699456.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    features: [
      'Mock interview sessions with detailed feedback',
      'Preparation for common veterinary interview questions',
      'Guidance on presenting your academic and personal achievements',
      'Communication skills training for effective interview performance',
      'Strategies for handling challenging questions',
      'Tips on appropriate dress code and interview etiquette'
    ],
    process: [
      { step: 1, title: 'Interview Assessment', description: 'Evaluating your current interview skills and identifying areas for improvement.' },
      { step: 2, title: 'Content Preparation', description: 'Reviewing important veterinary topics and preparing answers to common questions.' },
      { step: 3, title: 'Mock Interviews', description: 'Conducting practice interviews in a simulated environment with expert feedback.' },
      { step: 4, title: 'Communication Training', description: 'Improving verbal and non-verbal communication skills for effective presentation.' },
      { step: 5, title: 'Final Preparation', description: 'Last-minute tips and confidence-building exercises before your actual interview.' }
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'How many mock interview sessions are included in the preparation package?',
        content: 'Our standard interview preparation package includes three comprehensive mock interview sessions with detailed feedback after each session. Additional sessions can be arranged based on individual requirements. Each session progressively increases in difficulty to help you build confidence and improve your performance.'
      },
      {
        id: 'faq-2',
        title: 'What types of questions are typically asked in veterinary college interviews?',
        content: 'Veterinary college interviews typically include questions about your motivation for choosing veterinary science, your understanding of the profession, basic scientific concepts related to biology and animal health, ethical scenarios in veterinary practice, your strengths and weaknesses, and how you handle challenges. We prepare you for all these categories of questions.'
      },
      {
        id: 'faq-3',
        title: 'Can you help with specific college interview patterns?',
        content: 'Yes, we have information about the interview patterns and focus areas of different veterinary colleges. We customize your preparation based on the specific colleges you\'re applying to, incorporating their known interview styles, panel compositions, and evaluation criteria to make your preparation as relevant as possible.'
      }
    ]
  },
  'scholarship': {
    title: 'Scholarship Guidance',
    subtitle: 'Making veterinary education more affordable through scholarships',
    icon: <Award size={32} />,
    description: 'Our scholarship guidance service helps you identify and apply for scholarships that can make your veterinary education more affordable. We provide information on government, institutional, and private scholarships available for BVSc students.',
    backgroundImage: 'https://images.pexels.com/photos/4492135/pexels-photo-4492135.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1',
    features: [
      'Information on various scholarship opportunities for veterinary students',
      'Eligibility assessment for different scholarships',
      'Guidance on preparing compelling scholarship applications',
      'Assistance with essay writing and personal statements',
      'Application review and submission support',
      'Follow-up on scholarship application status'
    ],
    process: [
      { step: 1, title: 'Scholarship Research', description: 'Identifying relevant scholarships based on your profile and eligibility.' },
      { step: 2, title: 'Eligibility Assessment', description: 'Determining which scholarships you qualify for and prioritizing applications.' },
      { step: 3, title: 'Application Preparation', description: 'Guidance on completing scholarship applications effectively.' },
      { step: 4, title: 'Essay and Statement Writing', description: 'Assistance with crafting compelling personal statements and essays.' },
      { step: 5, title: 'Submission and Follow-up', description: 'Support during the submission process and tracking application status.' }
    ],
    faqs: [
      {
        id: 'faq-1',
        title: 'What types of scholarships are available for veterinary students in India?',
        content: 'Veterinary students in India can access various scholarships including government scholarships (central and state), institutional scholarships offered by veterinary colleges, merit-based scholarships from educational trusts, category-based scholarships (SC/ST/OBC), scholarships for economically weaker sections, and private scholarships from corporations and foundations interested in animal welfare and veterinary education.'
      },
      {
        id: 'faq-2',
        title: 'When should I start applying for scholarships?',
        content: 'You should start researching and applying for scholarships as soon as you receive your admission confirmation. Many scholarships have specific application windows at the beginning of the academic year. However, some scholarships may be available throughout the year. We help you create a scholarship calendar to ensure you don\'t miss any opportunities.'
      },
      {
        id: 'faq-3',
        title: 'Can I apply for multiple scholarships simultaneously?',
        content: 'Yes, you can and should apply for multiple scholarships simultaneously to maximize your chances of receiving financial aid. There is generally no restriction on the number of scholarships you can apply for, though there may be limitations on how many you can receive. We help you manage multiple applications efficiently to meet all requirements and deadlines.'
      }
    ]
  }
};

interface ServiceDetailPageProps {
  serviceId: string;
}

const ServiceDetailPage: React.FC<ServiceDetailPageProps> = ({ serviceId }) => {
  const navigate = useNavigate();
  const service = serviceData[serviceId as keyof typeof serviceData];
  
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    
    // Redirect if service doesn't exist
    if (!service) {
      navigate('/services', { replace: true });
      return;
    }
    
    // Update document title
    document.title = `${service.title} - VetEdu Consultants`;
  }, [service, navigate, serviceId]);

  if (!service) return null;

  return (
    <>
      <Banner 
        title={service.title}
        subtitle={service.subtitle}
        backgroundImage={service.backgroundImage}
      />
      
      <section className="section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-2 flex items-center">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg mr-4">
                  {service.icon}
                </div>
                <h2 className="text-3xl font-bold text-primary-700">
                  {service.title}
                </h2>
              </div>
              
              <p className="text-lg text-gray-700 mb-6 mt-4">
                {service.description}
              </p>
              
              <h3 className="text-xl font-semibold mb-4 text-primary-700">
                Key Features
              </h3>
              
              <ul className="space-y-3 mb-8">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check size={20} className="text-primary-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <a href="#contact-section" className="btn btn-primary">
                Inquire About This Service
              </a>
            </div>
            
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img 
                src={service.backgroundImage} 
                alt={service.title} 
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Our Process" 
            subtitle={`How our ${service.title.toLowerCase()} service works`}
          />
          
          <div className="max-w-4xl mx-auto mt-12">
            <Timeline items={service.process} />
          </div>
        </div>
      </section>
      
      <section id="contact-section" className="section">
        <div className="container-custom">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="p-8 md:p-12 bg-primary-700 text-white">
                <h2 className="text-3xl font-bold mb-6 text-white">
                  Interested in Our {service.title}?
                </h2>
                
                <p className="text-gray-100 mb-6">
                  Fill out the form to learn more about how our {service.title.toLowerCase()} can help you achieve your veterinary education goals.
                </p>
                
                <ul className="space-y-4 mb-8">
                  {service.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check size={20} className="text-secondary-500 mr-2 mt-1 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="bg-primary-800 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-4 text-white">
                    Why Choose Our Service
                  </h3>
                  <p className="text-gray-200">
                    Our experienced team has helped hundreds of students successfully navigate the BVSc admission process. We provide personalized support tailored to your specific needs and goals.
                  </p>
                </div>
              </div>
              
              <div className="p-8 md:p-12">
                <h3 className="text-2xl font-semibold mb-6 text-primary-700">
                  Request Information
                </h3>
                
                <form className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-1">
                      Full Name*
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="form-input"
                      placeholder="Enter your full name"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-1">
                      Email Address*
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="form-input"
                      placeholder="Enter your email"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-1">
                      Phone Number*
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="form-input"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-gray-700 font-medium mb-1">
                      Your Message*
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      className="form-input"
                      placeholder="Tell us about your requirements"
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    className="btn btn-primary w-full"
                  >
                    Submit Inquiry
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="section bg-gray-50">
        <div className="container-custom">
          <SectionTitle 
            title="Frequently Asked Questions" 
            subtitle={`Common questions about our ${service.title.toLowerCase()} service`}
          />
          
          <div className="max-w-3xl mx-auto mt-8">
            <Accordion items={service.faqs} />
          </div>
        </div>
      </section>
      
      <CallToAction 
        title="Ready to Get Started?"
        description={`Contact us today to learn more about our ${service.title.toLowerCase()} service and how we can help you achieve your veterinary education goals.`}
        buttonText="Contact Us"
        buttonLink="/contact"
      />
    </>
  );
};

export default ServiceDetailPage;